/**
 * Google Drive Zustand Store
 *
 * Centralized state management for Google Drive connection, folder selection,
 * and file listings with AsyncStorage persistence.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import type {
  GoogleDriveFile,
  GoogleDriveFolder,
  GoogleDriveConnectionState,
} from './types';
import { getGoogleTokens, clearGoogleTokens, isTokenValid } from './auth';
import {
  listFiles,
  listFolders,
  checkDriveConnection,
  getDriveUserInfo,
} from './api';

interface GoogleDriveState {
  // Connection state
  connectionState: GoogleDriveConnectionState;
  connectedEmail: string | null;

  // Selected data room folder
  selectedFolder: GoogleDriveFolder | null;

  // Current folder view (for browsing)
  currentFolderId: string;
  currentFolderPath: GoogleDriveFolder[];

  // Files in current folder
  files: GoogleDriveFile[];
  folders: GoogleDriveFolder[];
  isLoadingFiles: boolean;
  nextPageToken: string | null;

  // Error state
  error: string | null;

  // Actions
  setConnectionState: (state: GoogleDriveConnectionState) => void;
  setConnectedEmail: (email: string | null) => void;
  setSelectedFolder: (folder: GoogleDriveFolder | null) => void;
  setCurrentFolder: (folderId: string, path: GoogleDriveFolder[]) => void;

  // Async actions
  checkConnection: () => Promise<boolean>;
  loadFilesAndFolders: (folderId?: string) => Promise<void>;
  loadMoreFiles: () => Promise<void>;
  disconnect: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  connectionState: 'disconnected' as GoogleDriveConnectionState,
  connectedEmail: null as string | null,
  selectedFolder: null as GoogleDriveFolder | null,
  currentFolderId: 'root',
  currentFolderPath: [] as GoogleDriveFolder[],
  files: [] as GoogleDriveFile[],
  folders: [] as GoogleDriveFolder[],
  isLoadingFiles: false,
  nextPageToken: null as string | null,
  error: null as string | null,
};

export const useGoogleDriveStore = create<GoogleDriveState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setConnectionState: (connectionState) => set({ connectionState }),

      setConnectedEmail: (connectedEmail) => set({ connectedEmail }),

      setSelectedFolder: (selectedFolder) => set({ selectedFolder }),

      setCurrentFolder: (currentFolderId, currentFolderPath) =>
        set({ currentFolderId, currentFolderPath }),

      checkConnection: async () => {
        try {
          const tokens = await getGoogleTokens();
          if (!tokens || !isTokenValid(tokens)) {
            set({ connectionState: 'disconnected', connectedEmail: null });
            return false;
          }

          const isConnected = await checkDriveConnection();
          if (isConnected) {
            const userInfo = await getDriveUserInfo();
            set({
              connectionState: 'connected',
              connectedEmail: userInfo?.email || null,
            });
            return true;
          }

          set({ connectionState: 'disconnected', connectedEmail: null });
          return false;
        } catch (error) {
          set({
            connectionState: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Connection check failed',
          });
          return false;
        }
      },

      loadFilesAndFolders: async (folderId?: string) => {
        const targetFolderId = folderId || get().currentFolderId;
        set({ isLoadingFiles: true, error: null });

        try {
          // Load folders and files in parallel
          const [foldersResult, filesResult] = await Promise.all([
            listFolders(targetFolderId),
            listFiles(targetFolderId),
          ]);

          set({
            currentFolderId: targetFolderId,
            folders: foldersResult,
            files: filesResult.files,
            nextPageToken: filesResult.nextPageToken || null,
            isLoadingFiles: false,
          });
        } catch (error) {
          set({
            isLoadingFiles: false,
            error:
              error instanceof Error ? error.message : 'Failed to load files',
          });
        }
      },

      loadMoreFiles: async () => {
        const { nextPageToken, currentFolderId, files } = get();
        if (!nextPageToken) return;

        set({ isLoadingFiles: true });

        try {
          const result = await listFiles(currentFolderId, nextPageToken);
          set({
            files: [...files, ...result.files],
            nextPageToken: result.nextPageToken || null,
            isLoadingFiles: false,
          });
        } catch (error) {
          set({
            isLoadingFiles: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to load more files',
          });
        }
      },

      disconnect: async () => {
        await clearGoogleTokens();
        set(initialState);
      },

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-google-drive',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Only persist connection info and selected folder
        connectionState: state.connectionState,
        connectedEmail: state.connectedEmail,
        selectedFolder: state.selectedFolder,
      }),
    }
  )
);

// Convenience hooks for common selectors
export const useGoogleDriveConnection = () =>
  useGoogleDriveStore((s) => ({
    connectionState: s.connectionState,
    connectedEmail: s.connectedEmail,
    selectedFolder: s.selectedFolder,
  }));

export const useGoogleDriveFiles = () =>
  useGoogleDriveStore((s) => ({
    files: s.files,
    folders: s.folders,
    isLoading: s.isLoadingFiles,
    hasMore: !!s.nextPageToken,
    error: s.error,
  }));
